import { keyBy, get, uniq, map, sortBy } from 'lodash';

const links = {
  first: 'companies?page%5Bnumber%5D=1&page%5Bsize%5D=15&sort=name',
  last: 'companies?page%5Bnumber%5D=1&page%5Bsize%5D=15&sort=name',
};

export const setRelationshipsDataToItem = (item, groupedIncluded) => {
  const vessel = groupedIncluded[`vessels:${get(item, 'relationships.vessel.data.id')}`];
  const crewUser = groupedIncluded[`clients:${get(item, 'relationships.client.data.id')}`];

  return ({
    id: item.id,
    createdAt: new Date(item.attributes.created_at).getTime(),
    vesselId: vessel ? vessel.id : null,
    name: vessel ? vessel.attributes.name : null,
    imo: vessel ? vessel.attributes.imo : null,
    owner_id: vessel ? vessel.attributes.owner_id : null,
    // CREW-1243 — the board marks a service vessel that only carries vacancies.
    advertising: vessel ? !!vessel.attributes.advertising : false,
    crewUser,
  });
};

/**
 * serializeAssignedVessels
 * This function is used in many places and each case includes different relationships
 * this function handle all types of relationships
 * @param data
 * @param groupedIncluded
 * @return {*}
 */
const serializeAssignedVessels = (data, groupedIncluded) => data
  .map((item) => setRelationshipsDataToItem(item, groupedIncluded))
  .sort((a, b) => a.createdAt - b.createdAt);

const sortAndAddCountToManningScale = (data, assignedVesselsByVesselId) => {
  const convertedData = data.map(({ id, attributes }) => ({
    id: `${id}:${attributes.role_id}:${attributes.vessel_id}`,
    originId: id,
    originRoleName: attributes.title,
    originRoleId: String(attributes.role_id),
    ...attributes,
    createdAt: new Date(attributes.created_at).getTime(),
    assignedVesselAt: assignedVesselsByVesselId[attributes.vessel_id].createdAt,
    imo: assignedVesselsByVesselId[attributes.vessel_id].imo,
    // CREW-1245 — the board's row selector orders vessel groups by name so the
    // rows line up with the label strip. Only the vessel knows its name, so it
    // has to travel down onto the manning scale.
    vesselName: assignedVesselsByVesselId[attributes.vessel_id].name,
  }));

  const sortedMs = sortBy(convertedData, [(i) => i.assignedVesselAt, 'crewing_priority', 'createdAt']);

  // add role count if we have same roles for the vessel
  return sortedMs.reduce((acc, currentItem, index, array) => {
    const prevItem = index > 0 ? acc[index - 1] : null;
    const nextItem = index + 1 !== array.length ? array[index + 1] : null;
    const isSamePriorityWithPrev = prevItem && currentItem.crewing_priority === prevItem.crewing_priority;
    const isSamePriorityWithNext = nextItem && currentItem.crewing_priority === nextItem.crewing_priority;

    const isAnotherShip = !prevItem || (prevItem.vessel_id !== currentItem.vessel_id);

    const count = isSamePriorityWithPrev && !isAnotherShip ? prevItem.count + 1 : 1;
    // name is a prop that we need for calendar plugin
    const name = !isSamePriorityWithPrev && !isSamePriorityWithNext
      ? currentItem.title
      : `${currentItem.title} ${count}`;

    return [...acc, { ...currentItem, count, name }];
  }, []);
};

export const convertItems = ({ data, included = [] }) => {
  // CREW-1243 — `advertising` has to be listed here, not only in
  // `setRelationshipsDataToItem` above. This is the path the BOARD takes:
  // `assignedVesselsByVesselId` built here is what `getVessels` spreads into
  // each row, and `ListOfVessels` reads the flag off that row. Missing it, the
  // four real `_AD_…` vessels drew no marker at all — the attribute is in the
  // payload, it was this destructure that dropped it.
  // CREW-1218 — `on_board_count` travels the same route and is normalised to a
  // number here: the label is a count on the row, so an absent field (an older
  // payload) has to read as 0 rather than reach the component as undefined.
  const serializedAssignedVessels = data.map(({id, attributes: {created_at, imo, owner_id, name, advertising, on_board_count}}) => (
    {id, vesselId: id, createdAt: new Date(created_at).getTime(), imo, owner_id, name, advertising: !!advertising, onBoardCount: Number(on_board_count) || 0}
  ));
  const assignedVesselsByVesselId = keyBy(serializedAssignedVessels, 'vesselId');
  const vesselIds = Object.keys(assignedVesselsByVesselId);

  const activeManningScales = included.filter((item) => (
    item.type === 'manning_scales'
    && item.attributes.state === 'active'
  ));

  const roleIds = uniq(map(activeManningScales, 'attributes.role_id'));

  return {
    data: {
      assignedVesselsByVesselId,
      manningScales: sortAndAddCountToManningScale(activeManningScales, assignedVesselsByVesselId),
    },
    roleIds,
    vesselIds,
    links,
    meta: { total: data.length },
  };
};

export const convertListOfItems = ({ data, included }) => {
  const groupedIncluded = keyBy(included, ({ id, type }) => `${type}:${id}`);

  return {
    data: serializeAssignedVessels(data, groupedIncluded),
    links,
    meta: { total: data.length },
  };
};

export const convertItem = ({ data, included }) => {
  const groupedIncluded = keyBy(included, ({ id, type }) => `${type}:${id}`);

  return setRelationshipsDataToItem(data, groupedIncluded);
};

export default convertItems;
